import type { CellContext, ColumnDefTemplate } from '@tanstack/react-table';
import { memo, useCallback, useEffect, useRef } from 'react';
import { useAppDispatch } from '@/redux/hooks';
import { updateDraftRow } from '@/redux/slices/table/slice';
import { cn } from '@/shared/utils';
import type { Hazard } from '@/types/swms';

// NOTE: Using important here as we have preflight styles from odin-components/myosh-login leaking and breaking the inputs
// This should be investigated and fixed so we don't have to use the 'important' modifier
const cellBaseClassNames = 'appearance-none rounded-sm border! p-1! text-sm focus:outline-hidden';
const inputClassNames = cn(cellBaseClassNames, 'flex w-full flex-1 border-gray-4!');
const divClassNames = cn(cellBaseClassNames, 'border-transparent!');

interface TextCellProps extends CellContext<Hazard, string | number> {
  editMode?: boolean;
}

const TextCell: ColumnDefTemplate<TextCellProps> = ({ getValue, column, row, editMode }) => {
  const initialValue = getValue();
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const columnMeta = column.columnDef.meta;

  useEffect(() => {
    if (editMode && inputRef.current) {
      inputRef.current.value = initialValue.toString();
    }
  }, [initialValue, editMode]);

  const handleOnBlur = useCallback(() => {
    if (!editMode || !inputRef.current) {
      return;
    }

    const newValue = inputRef.current.value.trim();
    if (newValue !== String(initialValue)) {
      dispatch(updateDraftRow({ data: { [column.id]: newValue }, rowIndex: row.index }));
    }
  }, [editMode, initialValue, dispatch, column.id, row.index]);

  return editMode ? (
    <input
      name={`row-${row.id}-column-${column.id}`}
      type={columnMeta?.type ?? 'text'}
      ref={inputRef}
      onBlur={handleOnBlur}
      min={columnMeta?.min ?? 1}
      max={columnMeta?.max ?? 5}
      placeholder={columnMeta?.placeholder ?? ''}
      className={inputClassNames}
    />
  ) : (
    <div className={divClassNames}>{row.original.isFirst ? initialValue : null}</div>
  );
};

export default memo(TextCell);
